@if (panel.isOpen()) {
  <!--
    Posição e teto de altura vêm do MESMO mapa que o gatilho usa, então o painel
    acompanha o botão sem coordenada calibrada à mão. `--palantir-chat-max-height` vai
    por binding de estilo, não por classe arbitrária: `calc()` exige espaço em volta do
    operador e propriedade arbitrária do Tailwind usa `_` como espaço.
  -->
  <section
    class="fixed z-[1200] w-[calc(100vw-2rem)] motion-safe:animate-[iara-panel-in_150ms_ease-out] sm:w-[420px]"
    [class]="layout().panel"
    [style.--palantir-chat-max-height]="layout().chatMaxHeight"
    data-testid="iara-panel"
    [attr.data-position]="panel.effectivePosition()"
  >
    <palantir-chat
      [persona]="persona"
      [messages]="chatMessages()"
      [isStreaming]="service.isStreaming()"
      [streamingText]="service.streamingText()"
      [welcomeTitle]="welcomeTitle()"
      [hasContext]="hasContext()"
      [contextLabel]="contextLabel()"
      [contextDismissible]="true"
      [avatarTemplate]="orbTemplate"
      [userAvatarTemplate]="userAvatarTemplate"
      [contentTemplate]="markdownTemplate"
      [messageTemplate]="messageExtrasTemplate"
      [headerActions]="headerActions"
      [(showSessions)]="historyOpen"
      [sessions]="chatSessions()"
      [sessionsTemplate]="sessionsTemplate"
      [bannerTemplate]="connection.isOffline() ? offlineTemplate : undefined"
      [blockedTemplate]="service.creditLimitReached() ? creditLimitTemplate : undefined"
      (messageSend)="onSend($event)"
      (contextDismiss)="onDismissContext()"
      (headerAction)="onHeaderAction($event)"
      (newChat)="onNewConversation()"
      (closed)="onClose()"
    />
  </section>

  <!-- `size` vem do `palantir-chat`: 24 no cabeçalho, 56 nas boas-vindas, 16 no
       rótulo de cada resposta. -->
  <ng-template #orbTemplate let-size>
    <palantir-iara-orb [size]="size" label="Iara" />
  </ng-template>

  <!--
    `palantir-avatar` resolve as iniciais a partir de `fallbackText` e já troca a
    imagem por elas quando o `<img>` falha ao carregar. `5xs` (20px) é o menor
    tamanho disponível e o mais próximo do ícone de 14px que ele substitui.
  -->
  <ng-template #userAvatarTemplate>
    <palantir-avatar
      size="5xs"
      shape="avatar"
      [fallbackText]="userName()"
      data-testid="iara-panel-user-avatar"
    >
      @if (userAvatarUrl(); as url) {
        <img [src]="url" [alt]="userName()" />
      }
    </palantir-avatar>
  </ng-template>

  <!--
    Histórico como gaveta, não navegação: escolher uma conversa a abre NESTE painel,
    sem tirar o usuário da tela em que ele está — que é a razão de o painel existir.

    Os seis outputs estão ligados, não só `select`: a lista renderiza o menu de
    renomear/excluir sem input para escondê-lo, então deixar os dois desligados criaria
    affordance quebrada.
  -->
  <ng-template #sessionsTemplate>
    <palantir-iara-conversation-list
      data-testid="iara-panel-conversation-list"
      [conversations]="conversations()"
      [activeId]="service.activeSessionId()"
      [serverSearch]="true"
      [hasMore]="service.sessionsHasMore()"
      [loadingMore]="service.sessionsLoadingMore()"
      emptyTitle="Nenhuma conversa ainda"
      emptyDescription="Pergunte algo à Iara para começar."
      (select)="onSelectSession($event)"
      (searchChange)="onSearchSessions($event)"
      (loadMore)="onLoadMoreSessions()"
      (newConversation)="onNewConversation()"
      (rename)="onRenameSession($event)"
      (deleteConversation)="onDeleteSession($event)"
    />
  </ng-template>

  <ng-template #markdownTemplate let-content>
    <app-chat-markdown [content]="content" />
  </ng-template>

  <ng-template #messageExtrasTemplate let-message>
    @if (message.reasoning) {
      <palantir-iara-reasoning [reasoning]="message.reasoning" [collapsible]="true" />
    }

    @if (message.failed) {
      <palantir-iara-callout
        data-testid="iara-panel-message-error"
        variant="negative"
        icon="warning-outline"
        title="Não foi possível gerar a resposta"
        [actionLabel]="message.retryable ? 'Tentar novamente' : ''"
        actionIcon="refresh-outline"
        (action)="onRetry(message.id)"
      />
    }

    @for (component of message.components ?? []; track $index) {
      <app-chat-component-host
        [component]="asChatComponent(component)"
        [actionStates]="service.actionStates()[asChatComponent(component).componentId]"
      />
    }

    @if (message.content && !message.failed) {
      <palantir-iara-turn-actions [content]="message.content" />
    }
  </ng-template>

  <ng-template #offlineTemplate>
    <palantir-iara-connection-banner data-testid="iara-panel-offline" />
  </ng-template>

  <ng-template #creditLimitTemplate>
    <palantir-iara-callout
      data-testid="iara-panel-credit-limit"
      variant="neutral"
      icon="rocket-launch-outline"
      title="Créditos da Iara esgotados"
      description="Fale com quem administra a conta para liberar mais créditos."
    />
  </ng-template>
}
